import React from 'react'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'
import BreadCrumb from 'components/BreadCrumb'
import Btn from 'components/Btn'

const TONES = {
  success: { tile: 'bg-success bg-opacity-5', value: 'text-success', icon: 'bg-success' },
  purple: { tile: 'bg-purple bg-opacity-5', value: 'text-purple', icon: 'bg-purple' },
  primary: { tile: 'bg-primary bg-opacity-5', value: 'text-primary', icon: 'bg-primary' },
  warning: { tile: 'bg-orange-500 bg-opacity-5', value: 'text-orange-500', icon: 'bg-orange-500' }
}

function StatTile({ icon: Icon, label, value, tone }) {
  const { tile, value: valueColor, icon: iconBg } = TONES[tone] || TONES.primary

  return (
    <div className={`w-full rounded-2xl p-4 ${tile}`}>
      <div className="flex items-start justify-between mb-1">
        <p className="text-sm text-secondary">{label}</p>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${iconBg}`}>
          <Icon size={18} className="text-white" />
        </div>
      </div>
      <p className={`text-xl font-bold ${valueColor}`}>{value}</p>
    </div>
  )
}

function Card({ title, action, children }) {
  return (
    <div className="w-full bg-white border border-border-lines rounded-2xl p-6 mb-5">
      <div className="flex items-center justify-between gap-4 mb-4">
        <p className="text-sm font-semibold text-dark-blue tracking-wide uppercase">{title}</p>
        {action}
      </div>
      {children}
    </div>
  )
}

function DetailRow({ label, value, valueClassName = 'text-dark-blue' }) {
  if (!value) return null
  return (
    <div className="grid grid-cols-[180px,1fr] gap-2 py-1.5">
      <p className="text-secondary">{label}</p>
      <p className={valueClassName}>{value}</p>
    </div>
  )
}

function StackedDetail({ label, value }) {
  if (!value) return null
  return (
    <div>
      <p className="text-xs text-secondary mb-1">{label}</p>
      <p className="text-sm text-dark-blue">{value}</p>
    </div>
  )
}

export default withTranslation()(function WinnerDetail({ t, winner, onBack, onViewImage }) {
  return (
    <section className="w-full max-w-4xl mx-auto pb-10">
      <div className="mb-5">
        <BreadCrumb
          items={[
            { name: t('payout.title'), url: '/payout' },
            { name: t('payout.winner_detail.breadcrumb') }
          ]}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-4 mb-5">
        <StatTile icon={UIcons.UilCheckCircle} label={t('payout.winner_detail.stats.winner_status')} value={winner.statusLabel ?? winner.status} tone="success" />
        <StatTile icon={UIcons.UilGift} label={t('payout.winner_detail.stats.prize_type')} value={winner.prizeType} tone="purple" />
        <StatTile icon={UIcons.UilWallet} label={t('payout.winner_detail.stats.prize_value')} value={winner.prizeValue} tone="primary" />
        <StatTile icon={UIcons.UilClockThree} label={t('payout.winner_detail.stats.payout_date')} value={winner.payoutDate} tone="warning" />
      </div>

      <Card title={t('payout.winner_detail.profile.title')}>
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 rounded-full bg-primary bg-opacity-10 flex items-center justify-center shrink-0">
            <UIcons.UilUser className="text-primary" size={28} />
          </div>

          <div className="flex-1">
            <DetailRow label={t('payout.winner_detail.profile.winner_name')} value={winner.name} />
            <DetailRow label={t('payout.winner_detail.profile.phone_number')} value={winner.phone} />
            <DetailRow label={t('payout.winner_detail.profile.location')} value={winner.location} />
            <DetailRow label={t('payout.winner_detail.profile.campaign')} value={winner.campaign} />
            <DetailRow label={t('payout.winner_detail.profile.prize_category')} value={winner.prizeCategory} />
            <DetailRow label={t('payout.winner_detail.profile.prize_name')} value={winner.prizeName} />
            <DetailRow label={t('payout.winner_detail.profile.payout_reference')} value={winner.payoutReference} />
            <DetailRow label={t('payout.winner_detail.profile.winner_batch')} value={winner.winnerBatch} />
            <DetailRow label={t('payout.winner_detail.profile.verification_method')} value={winner.verificationMethod} />
            <DetailRow label={t('payout.winner_detail.profile.status')} value={winner.statusLabel ?? winner.status} valueClassName="text-success font-medium" />
            <DetailRow label={t('payout.winner_detail.profile.date_won')} value={winner.dateWon} />
            <DetailRow label={t('payout.winner_detail.profile.note')} value={winner.note} />
          </div>
        </div>
      </Card>

      <Card
        title={t('payout.winner_detail.proof.title')}
        action={
          winner.proof && (
            <div className="flex items-center gap-2">
              <Btn
                content={() => <span className="flex items-center gap-1.5"><UIcons.UilImport size={14} /> {t('payout.winner_detail.proof.download_proof')}</span>}
                className="text-dark-blue bg-white border border-border-lines rounded-full h-9 px-4 font-medium text-sm"
                click={() => {}}
              />
              <Btn
                content={() => <span className="flex items-center gap-1.5"><UIcons.UilPlus size={14} /> {t('payout.winner_detail.proof.view_image')}</span>}
                className="text-white bg-primary rounded-full h-9 px-4 font-medium text-sm"
                click={onViewImage}
              />
            </div>
          )
        }
      >
        {
          winner.proof ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <StackedDetail label={t('payout.winner_detail.proof.uploaded_by')} value={winner.proof.uploadedBy} />
                <StackedDetail label={t('payout.winner_detail.proof.uploaded_date')} value={winner.proof.uploadedDate} />
                <StackedDetail label={t('payout.winner_detail.proof.file_type')} value={winner.proof.fileType} />
                <StackedDetail label={t('payout.winner_detail.proof.file_size')} value={winner.proof.fileSize} />
              </div>

              <div className="flex items-center justify-between gap-4 border border-border-lines rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-lg bg-background flex items-center justify-center shrink-0">
                    <UIcons.UilImage className="text-secondary" size={18} />
                  </span>
                  <div>
                    <p className="text-xs text-secondary">{t('payout.winner_detail.proof.photo_label')}</p>
                    <p className="text-sm text-dark-blue">{winner.proof.fileName}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button type="button" className="w-9 h-9 rounded-full border border-border-lines flex items-center justify-center hover:bg-background">
                    <UIcons.UilDownloadAlt size={16} className="text-secondary" />
                  </button>
                  <button type="button" onClick={onViewImage} className="w-9 h-9 rounded-full border border-border-lines flex items-center justify-center hover:bg-background">
                    <UIcons.UilEye size={16} className="text-secondary" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-secondary">{t('payout.winner_detail.proof.empty')}</p>
          )
        }
      </Card>

      <div className="grid md:grid-cols-2 gap-5">
        <Card title={t('payout.winner_detail.payout_prize_detail.title')}>
          <DetailRow label={t('payout.winner_detail.payout_prize_detail.funding_source')} value={winner.fundingSource} />
          <DetailRow label={t('payout.winner_detail.payout_prize_detail.prize_amount')} value={winner.prizeValue} valueClassName="text-success font-medium" />
          <DetailRow label={t('payout.winner_detail.payout_prize_detail.delivery_method')} value={winner.deliveryMethod} />
          <DetailRow label={t('payout.winner_detail.payout_prize_detail.merchant_branch')} value={winner.merchantBranch} />
          <DetailRow label={t('payout.winner_detail.payout_prize_detail.approved_by')} value={winner.approvedBy} />
          <DetailRow label={t('payout.winner_detail.payout_prize_detail.notes')} value={winner.note} />
        </Card>

        <Card title={t('payout.winner_detail.activity_timeline.title')}>
          <div className="flex flex-col gap-4">
            {
              (winner.timeline ?? []).map((step) => (
                <div key={step.key} className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded-full bg-success flex items-center justify-center shrink-0">
                    <UIcons.UilCheck className="text-white" size={12} />
                  </span>
                  <p className="flex-1 text-sm text-dark-blue">{t(`payout.winner_detail.activity_timeline.${step.key}`)}</p>
                  <p className="text-xs text-secondary whitespace-nowrap">{step.timestamp}</p>
                </div>
              ))
            }
          </div>
        </Card>
      </div>
    </section>
  )
})
